<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const workflowId = computed(() => route.params.workflowId);
const executions = ref([]);
const isLoading = ref(true);
const deletingId = ref(null);

const formatTs = ts => {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n)) return '—';
  return new Date(n * 1000).toLocaleString('pt-BR');
};

const statusClass = status => {
  const map = {
    running: 'bg-n-teal-3 text-n-teal-11',
    waiting: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-teal-4 text-n-teal-12',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return map[status] || 'bg-n-slate-3 text-n-slate-11';
};

const load = async () => {
  if (!workflowId.value) return;
  isLoading.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.executions(workflowId.value);
    executions.value = data?.payload?.executions ?? [];
  } catch {
    useAlert('Não foi possível carregar o histórico.');
    executions.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const goToEditor = () => {
  router.push({
    name: 'conversation_workflows_edit',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
    },
  });
};

const openExecution = row => {
  router.push({
    name: 'conversation_workflows_execution_detail',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
      executionId: String(row.id),
    },
  });
};

const deleteExecution = async (row, event) => {
  event?.stopPropagation?.();
  if (
    !window.confirm(
      `Apagar a execução #${row.id}? Esta ação não pode ser desfeita.`
    )
  ) {
    return;
  }
  deletingId.value = row.id;
  try {
    await conversationWorkflowsAPI.executionDelete(workflowId.value, row.id);
    useAlert('Execução removida.');
    await load();
  } catch {
    useAlert('Não foi possível apagar a execução.');
  } finally {
    deletingId.value = null;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex flex-col bg-n-surface-1 text-n-slate-12 border-t border-n-weak"
  >
    <header
      class="flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2 sm:px-4"
    >
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        title="Voltar ao editor"
        @click="goToEditor"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-5 shrink-0"
          aria-hidden="true"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <h1 class="min-w-0 flex-1 truncate text-base font-medium text-n-slate-12">
        Historico
      </h1>
      <Button
        slate
        outline
        size="sm"
        label="Atualizar"
        :is-loading="isLoading"
        @click="load"
      />
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      Carregando…
    </div>

    <div
      v-else-if="executions.length === 0"
      class="flex flex-1 items-center justify-center px-4 text-center text-n-slate-11"
    >
      Nenhuma execução registrada para este fluxo.
    </div>

    <div
      v-else
      class="min-h-0 flex-1 overflow-auto p-3 sm:p-4"
    >
      <div
        class="overflow-x-auto rounded-2xl border border-n-weak bg-n-solid-1"
      >
        <table class="w-full min-w-[56rem] border-collapse text-left text-sm">
          <thead>
            <tr
              class="border-b border-n-weak bg-n-alpha-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
            >
              <th class="px-3 py-3">ID</th>
              <th class="px-3 py-3">Status</th>
              <th class="px-3 py-3">Gatilho</th>
              <th class="px-3 py-3">Conversa</th>
              <th class="px-3 py-3">Passos</th>
              <th class="px-3 py-3">Início</th>
              <th class="px-3 py-3">Fim</th>
              <th class="px-3 py-3">Erro</th>
              <th class="w-px px-3 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="row in executions"
              :key="row.id"
              class="cursor-pointer hover:bg-n-alpha-2/70"
              role="button"
              tabindex="0"
              @click="openExecution(row)"
              @keydown.enter="openExecution(row)"
            >
              <td class="px-3 py-2.5 font-mono text-n-slate-11">
                {{ row.id }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium capitalize"
                  :class="statusClass(row.status)"
                >
                  {{ row.status }}
                </span>
              </td>
              <td class="max-w-[12rem] truncate px-3 py-2.5 text-n-slate-12" :title="row.trigger_kind">
                {{ row.trigger_kind }}
              </td>
              <td class="px-3 py-2.5 font-mono text-n-slate-11">
                {{ row.conversation_id }}
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ row.steps_executed }}
              </td>
              <td class="whitespace-nowrap px-3 py-2.5 text-n-slate-11">
                {{ formatTs(row.started_at) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2.5 text-n-slate-11">
                {{ formatTs(row.finished_at) }}
              </td>
              <td class="max-w-[14rem] truncate px-3 py-2.5 text-n-ruby-11" :title="row.error_message || ''">
                {{ row.error_message || '—' }}
              </td>
              <td class="px-3 py-2.5 text-right">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-9 disabled:pointer-events-none disabled:opacity-40"
                  title="Apagar execução"
                  :disabled="deletingId !== null"
                  @click="deleteExecution(row, $event)"
                >
                  <span
                    v-if="deletingId === row.id"
                    class="size-4 animate-spin rounded-full border-2 border-n-slate-8 border-t-transparent"
                    aria-hidden="true"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-[18px] shrink-0"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    <line x1="10" x2="10" y1="11" y2="17" />
                    <line x1="14" x2="14" y1="11" y2="17" />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
